import { useEffect, useState } from 'react';
import {
  isUserWithRoles, useCombinedUser, useIsMsalAuth, useIsUserAuth, UserRoles,
} from '@equifinance/shared';
import { Stages } from '../../types/Stages';
import { useAccountContext } from '../account/index';
import { ApplicationPageType } from '../../components/AccountTabNav';

export function usePageConfiguration(page: ApplicationPageType) {
  const [isReadOnly, setIsReadOnly] = useState<boolean>(false);
  const isExternalUser = useIsUserAuth();
  const isInternalUser = useIsMsalAuth();
  const { user } = useCombinedUser();
  const { account: { status, stage } } = useAccountContext();

  const isUnderwriter = isInternalUser && isUserWithRoles([UserRoles.UNDERWRITER], user);
  const isSalesSupport = isInternalUser && isUserWithRoles([UserRoles.SALES_SUPPORT], user);
  const isReadOnlyUser = isInternalUser && isUserWithRoles([UserRoles.READ_ONLY], user);
  const isBrokerAdmin = isExternalUser && isUserWithRoles([UserRoles.BROKER_ADMIN], user);
  const isBrokerUser = isExternalUser && isUserWithRoles([UserRoles.BROKER_USER], user);

  useEffect(() => {
    if (!status) return;

    switch (page) {
      case ApplicationPageType.CASE_DETAILS:
      case ApplicationPageType.CREDIT:
      case ApplicationPageType.PRODUCT:
        if (isUnderwriter) {
          const productDisabled = page === ApplicationPageType.PRODUCT && [Stages.Offer, Stages.Completion].includes(stage);
          setIsReadOnly((status !== 'WithBroker' && status !== 'WithLender') || productDisabled);
        } else if (isSalesSupport) {
          setIsReadOnly(status !== 'WithBroker');
        } else if (isReadOnlyUser) {
          setIsReadOnly(true);
        } else if (isBrokerAdmin) {
          setIsReadOnly(status !== 'WithBroker');
        } else if (isBrokerUser) {
          setIsReadOnly(status !== 'WithBroker');
        }
        break;

      case ApplicationPageType.CHECKLIST:
        if (isUnderwriter) {
          setIsReadOnly(status !== 'WithBroker' && status !== 'WithLender' && status !== 'Withdrawn');
        } else if (isSalesSupport) {
          setIsReadOnly(true);
        } else if (isReadOnlyUser) {
          setIsReadOnly(true);
        }
        break;

      case ApplicationPageType.TASKS:
        if (isReadOnlyUser) {
          setIsReadOnly(true);
        }
        break;

      case ApplicationPageType.AUDIT:
        setIsReadOnly(false);
        break;

      default:
    }
  }, [page, status]);

  return {
    isReadOnly,
  };
}
